import type { ElementType, ReactNode } from 'react';

/**
 * Bandeau d'état (audit UX/UI passe 2, B1) : une seule définition pour un
 * motif répété à l'identique dans presque tous les écrans
 * (`rounded-card bg-*-bg px-3 py-2 text-sm text-*`) — App.tsx, RecipeForm.tsx,
 * RecipeDetail.tsx, RecipeList.tsx, ImportUrlForm.tsx, ImportPasteForm.tsx,
 * CookLogForm.tsx, DataBackup.tsx. Le rôle ARIA par défaut suit le ton :
 * `alert` pour une erreur (annoncé immédiatement), `status` pour le reste
 * (annoncé sans interrompre), toujours substituable si un écran a besoin
 * d'un rôle différent.
 */
export type BannerTone = 'danger' | 'warning' | 'success' | 'info';

const TONE_CLASSES: Record<BannerTone, string> = {
  danger: 'bg-danger-bg text-danger',
  warning: 'bg-warning-bg text-warning',
  success: 'bg-success-bg text-success',
  info: 'bg-info-bg text-info',
};

const DEFAULT_ROLE: Record<BannerTone, 'alert' | 'status'> = {
  danger: 'alert',
  warning: 'status',
  success: 'status',
  info: 'status',
};

export interface BannerProps {
  tone: BannerTone;
  /** `'div'` pour un bandeau qui contient plus qu'un texte simple (ex. un bouton d'action) — `'p'` par défaut. */
  as?: ElementType;
  role?: 'alert' | 'status';
  className?: string;
  children: ReactNode;
}

export function Banner({ tone, as = 'p', role, className = '', children }: BannerProps): React.JSX.Element {
  const Tag = as;
  return (
    // `animate-fade-in` : un bandeau qui se pose sans transition décale d'un
    // coup tout ce qui est en dessous, et l'œil rate ce qui vient d'arriver
    // précisément parce que rien n'a bougé. C'est le seul endroit de
    // l'application où un contenu **s'ajoute** au milieu d'une page déjà lue.
    //
    // `px-4 py-3` plutôt que `px-3 py-2` : à 8 px de padding vertical, un
    // message d'erreur sur deux lignes touchait les bords de son propre fond.
    <Tag role={role ?? DEFAULT_ROLE[tone]} className={`animate-fade-in rounded-card px-4 py-3 text-sm ${TONE_CLASSES[tone]} ${className}`.trim()}>
      {children}
    </Tag>
  );
}
